import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Modal,
  Spin,
  Switch,
  message,
} from '@surecompanies/core_components'
import InputNumber from 'antd/lib/input-number'
import Input from 'antd/lib/input'
import type { InputRef } from 'antd/lib/input'
import {
  CheckCircleFilled,
  CheckOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  MinusCircleOutlined,
  SendOutlined,
  TableOutlined,
} from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import {
  createPlanAndRateOverrideRule,
  updatePlanAndRateOverrideRule,
  updatePlanAndRateOverrideRuleStatus,
} from '../../PlanOverrides/api/plan-and-rate-override-rules-api'
import {
  presignUploadUrl,
  completeUpload,
} from '../../PlanOverrides/api/plan-override-documents-api'
import { uploadToS3 } from '../../PlanOverrides/utils/uploadToS3'
import { useEditDocumentStaging } from '../../PlanOverrides/hooks/useEditDocumentStaging'
import {
  ALLOWED_MIME_TYPES,
  MAX_SIZE_BY_MIME,
} from '../../PlanOverrides/utils/fileValidation'
import { InlineEvidenceSection } from './InlineEvidenceSection'
import {
  PlanAndRateOverrideRuleStatus,
  PlanAndRateOverrideValueSource,
} from '../../PlanOverrides/pages/PlanAndRateOverrides/enums/plan-and-rate-override-rule.enum'
import {
  useFindPlanAndRateRuleByMatch,
  FIND_PLAN_AND_RATE_RULE_QUERY_KEY,
} from '../../PlanOverrides/pages/PlanAndRateOverrides/hooks/useFindPlanAndRateRuleByMatch'
import type { PlanAndRateOverrideRuleResponseDto } from '../../PlanOverrides/api/dto/plan-and-rate-override-rules.dto'
import {
  FIELD_CONFIG,
  IDENTITY_FIELDS,
  AREA_FACTOR_KEYS,
  coerceParamValue,
  flattenRuleParams,
  nestRuleParams,
  getStagingValue,
  type FieldValueType,
} from '../utils/planAndRateFields'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

const SRC_IDEON = PlanAndRateOverrideValueSource.IDEON_DATA
const SRC_REAL = PlanAndRateOverrideValueSource.REAL_DATA
const SRC_OVERRIDE = PlanAndRateOverrideValueSource.OVERRIDE_DATA

type RowState = {
  source: PlanAndRateOverrideValueSource
  overrideValue: string | number | boolean | null
}

export interface PlanAndRateReviewCellInfo {
  rowData: Record<string, unknown>
}

export interface PlanAndRateReviewSavedUpdate {
  params: Record<string, unknown>
  paramSources: Record<string, string>
  status: string
}

interface Props {
  open: boolean
  onClose: () => void
  cellInfo: PlanAndRateReviewCellInfo | null
  onSaved?: (rowId: string, update: PlanAndRateReviewSavedUpdate) => void
}

// ---------------------------------------------------------------------------
// Theme tokens
// ---------------------------------------------------------------------------

const BRAND = '#0d9488'
const BRAND_LIGHT = '#ccfbf1'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const currencyFormatter = (
  v: number | string | undefined,
  precision?: number
): string => {
  if (v === undefined || v === null || v === '') return ''
  const n = typeof v === 'string' ? Number(v) : v
  if (isNaN(n)) return String(v)
  if (precision !== undefined) return n.toFixed(precision)
  return n.toLocaleString('en-US')
}

const currencyParser = (v: string | undefined): number | string => {
  if (!v) return ''
  const s = v.replace(/[$,\s]/g, '')
  const n = Number(s)
  return isNaN(n) ? s : n
}

function formatValue(
  value: unknown,
  valueType: FieldValueType,
  precision?: number
): string {
  if (valueType === 'boolean') return value ? 'Yes' : 'No'
  if (value === null || value === undefined || value === '') return '—'
  if (valueType === 'currency' || valueType === 'number') {
    const n = Number(value)
    if (!isNaN(n)) {
      if (n === 0 && precision !== undefined) return '—'
      if (precision !== undefined) return n.toFixed(precision)
      return n.toLocaleString('en-US', { maximumFractionDigits: 4 })
    }
  }
  return String(value)
}

function ReadonlyBoolCell({ value }: { value: unknown }) {
  return (
    <span style={{ fontSize: 13, fontWeight: 600 }}>
      {value ? 'Yes' : 'No'}
    </span>
  )
}

function buildInitialRows(
  fields: string[],
  rule: PlanAndRateOverrideRuleResponseDto | null,
  rowData: Record<string, unknown>
): Record<string, RowState> {
  const flatParams = rule?.params
    ? flattenRuleParams(rule.params as Record<string, unknown>)
    : {}
  const flatSources = rule?.paramSources ?? {}
  return Object.fromEntries(
    fields.map((field) => {
      const recordedSource = (flatSources as Record<string, string>)[field]
      const source: PlanAndRateOverrideValueSource =
        (recordedSource as PlanAndRateOverrideValueSource) ?? SRC_IDEON
      const isBool = FIELD_CONFIG[field]?.valueType === 'boolean'
      const stagingVal = getStagingValue(field, rowData)
      const fallback = isBool
        ? Boolean(stagingVal ?? false)
        : ((stagingVal as string | number | boolean | null) ?? null)
      const overrideValue =
        flatParams[field] !== undefined
          ? (flatParams[field] as string | number | boolean | null)
          : fallback
      return [field, { source, overrideValue }]
    })
  )
}

const COL_LABEL_STYLE: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
}

// ---------------------------------------------------------------------------
// OverrideInput
// ---------------------------------------------------------------------------

function OverrideInput({
  field,
  value,
  disabled,
  autoFocus,
  onChange,
}: {
  field: string
  value: string | number | boolean | null
  disabled: boolean
  autoFocus?: boolean
  onChange: (v: string | number | boolean | null) => void
}) {
  const cfg = FIELD_CONFIG[field]
  const valueType = cfg?.valueType ?? 'string'
  const precision = cfg?.precision
  const inputRef = useRef<InputRef | null>(null)

  useEffect(() => {
    if (!autoFocus || disabled || valueType === 'boolean') return
    const id = window.setTimeout(() => {
      inputRef.current?.focus?.({ cursor: 'all' })
      const active = document.activeElement
      if (active instanceof HTMLInputElement) active.select()
    }, 0)
    return () => window.clearTimeout(id)
  }, [autoFocus, disabled, valueType])

  if (valueType === 'boolean') {
    return (
      <Switch
        checked={value === true}
        disabled={disabled}
        onChange={(checked) => onChange(checked)}
        size="small"
      />
    )
  }
  if (valueType === 'currency' || valueType === 'number') {
    return (
      <InputNumber
        ref={inputRef as unknown as React.Ref<HTMLInputElement>}
        value={value as number | null}
        disabled={disabled}
        onChange={(v) => onChange(v ?? null)}
        formatter={(v) => currencyFormatter(v, precision)}
        parser={currencyParser as (v: string | undefined) => string | number}
        size="small"
        style={{ flex: 1, minWidth: 0, width: '100%' }}
      />
    )
  }
  return (
    <Input
      ref={inputRef}
      value={(value as string) ?? ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      size="small"
      style={{ flex: 1, minWidth: 0, width: '100%' }}
    />
  )
}

// ---------------------------------------------------------------------------
// FieldRow
// ---------------------------------------------------------------------------

const PILL_BASE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  borderRadius: 7,
  padding: '4px 8px',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.2,
}

const SELECTED_STYLE: React.CSSProperties = {
  ...PILL_BASE,
  background: '#ecfdf5',
  border: '1px solid #a7f3d0',
  color: '#15803d',
}

const UNSELECTED_STYLE: React.CSSProperties = {
  ...PILL_BASE,
  background: '#f8fafc',
  border: '1px solid #e5e7eb',
  color: '#94a3b8',
}

interface FieldRowProps {
  field: string
  isChecked: boolean
  isReadOnly: boolean
  rowState: RowState
  ideonValue: unknown
  realValue: unknown
  showRealData: boolean
  autoFocusOverride: boolean
  onToggle: (field: string, checked: boolean) => void
  onSelect: (field: string, src: PlanAndRateOverrideValueSource) => void
  onOverrideChange: (field: string, v: string | number | boolean | null) => void
}

function FieldRow({
  field,
  isChecked,
  isReadOnly,
  rowState,
  ideonValue,
  realValue,
  showRealData,
  autoFocusOverride,
  onToggle,
  onSelect,
  onOverrideChange,
}: FieldRowProps) {
  const cfg = FIELD_CONFIG[field]
  const label = cfg?.label ?? field
  const valueType = cfg?.valueType ?? 'string'
  const precision = cfg?.precision
  const { source, overrideValue } = rowState

  const isDisabled = isReadOnly || !isChecked

  const cellStyle = (src: PlanAndRateOverrideValueSource) =>
    source === src ? SELECTED_STYLE : UNSELECTED_STYLE

  const renderDisplayVal = (val: unknown) =>
    valueType === 'boolean' ? (
      <ReadonlyBoolCell value={val ?? false} />
    ) : (
      <span>{formatValue(val, valueType, precision)}</span>
    )

  return (
    <tr
      style={{
        borderTop: '1px solid #f1f5f9',
        opacity: isChecked ? 1 : 0.45,
        transition: 'opacity 150ms ease',
      }}
    >
      <td
        style={{
          padding: '8px 12px',
          verticalAlign: 'middle',
          width: 36,
          textAlign: 'center',
        }}
      >
        <input
          type="checkbox"
          checked={isChecked}
          disabled={isReadOnly}
          onChange={(e) => onToggle(field, e.target.checked)}
          style={{
            cursor: isReadOnly ? 'default' : 'pointer',
            width: 14,
            height: 14,
          }}
        />
      </td>
      <td style={{ padding: '8px 12px', verticalAlign: 'middle' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
          {label}
        </div>
        {label.toLowerCase().replace(/\s+/g, '') !== field.toLowerCase() && (
          <div style={{ fontSize: 11, color: '#9ca3af' }}>{field}</div>
        )}
      </td>
      <td
        style={{
          padding: '8px 12px',
          verticalAlign: 'middle',
          cursor: isDisabled ? 'default' : undefined,
        }}
        onClick={() => !isDisabled && onSelect(field, SRC_IDEON)}
      >
        <div style={cellStyle(SRC_IDEON)}>
          {source === SRC_IDEON && (
            <CheckCircleFilled style={{ color: '#16a34a', fontSize: 13 }} />
          )}
          {renderDisplayVal(ideonValue)}
        </div>
      </td>
      {showRealData && (
        <td
          style={{
            padding: '8px 12px',
            verticalAlign: 'middle',
            cursor: isDisabled ? 'default' : undefined,
          }}
          onClick={() => !isDisabled && onSelect(field, SRC_REAL)}
        >
          <div style={cellStyle(SRC_REAL)}>
            {source === SRC_REAL && (
              <CheckCircleFilled style={{ color: '#16a34a', fontSize: 13 }} />
            )}
            {renderDisplayVal(realValue)}
          </div>
        </td>
      )}
      <td
        style={{
          padding: '8px 12px',
          verticalAlign: 'middle',
          minWidth: 150,
          cursor: isDisabled ? 'default' : undefined,
        }}
        onClick={() =>
          !isDisabled &&
          source !== SRC_OVERRIDE &&
          onSelect(field, SRC_OVERRIDE)
        }
      >
        <div
          style={{
            ...(source === SRC_OVERRIDE ? SELECTED_STYLE : UNSELECTED_STYLE),
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {source === SRC_OVERRIDE ? (
            <CheckCircleFilled
              style={{ color: '#16a34a', fontSize: 13, flexShrink: 0 }}
            />
          ) : (
            <MinusCircleOutlined
              style={{ color: '#d1d5db', fontSize: 13, flexShrink: 0 }}
            />
          )}
          <OverrideInput
            field={field}
            value={source === SRC_OVERRIDE ? overrideValue : null}
            disabled={source !== SRC_OVERRIDE || isDisabled}
            autoFocus={autoFocusOverride && !isDisabled}
            onChange={(v) => onOverrideChange(field, v)}
          />
        </div>
      </td>
    </tr>
  )
}

// ---------------------------------------------------------------------------
// StatusPill
// ---------------------------------------------------------------------------

function StatusPill({ status }: { status: string }) {
  const isApproved = status === PlanAndRateOverrideRuleStatus.APPROVED
  const isRejected = status === PlanAndRateOverrideRuleStatus.REJECTED
  const dotColor = isApproved ? '#16a34a' : isRejected ? '#dc2626' : '#f59e0b'
  const bg = isApproved ? '#f0fdf4' : isRejected ? '#fef2f2' : '#fffbeb'
  const border = isApproved ? '#bbf7d0' : isRejected ? '#fecaca' : '#fde68a'
  const label = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 6,
        padding: '2px 10px',
        fontSize: 13,
        fontWeight: 600,
        color: '#374151',
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: dotColor,
        }}
      />
      {label}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------

export default function PlanAndRateReviewModal({
  open,
  onClose,
  cellInfo,
  onSaved,
}: Props) {
  const queryClient = useQueryClient()

  const identity = useMemo(() => {
    if (!cellInfo) return null
    const d = cellInfo.rowData
    return {
      year: d.year as number,
      hiosPlanId: d.hiosPlanId as string,
      state: d.state as string,
    }
  }, [cellInfo])

  const { rule, isLoading: ruleLoading } = useFindPlanAndRateRuleByMatch(
    open ? identity : null
  )

  const PAR_RULE_BASE = '/api/v1/plan-and-rate-override-rules'

  const fields = useMemo(
    () => Object.keys(FIELD_CONFIG).filter((f) => !IDENTITY_FIELDS.has(f)),
    []
  )

  const ideonData = cellInfo?.rowData ?? {}
  const realData = (cellInfo?.rowData?._currentValue ?? {}) as Record<
    string,
    unknown
  >

  const [rows, setRows] = useState<Record<string, RowState>>({})
  const [focusField, setFocusField] = useState<string | null>(null)
  const [checkedFields, setCheckedFields] = useState<Set<string>>(new Set())
  const rowsInitializedRef = useRef(false)

  useEffect(() => {
    if (!open) {
      rowsInitializedRef.current = false
      return
    }
    if (ruleLoading || fields.length === 0 || rowsInitializedRef.current) return
    const built = buildInitialRows(fields, rule, ideonData)
    setRows(built)
    const flatParams = rule?.params
      ? flattenRuleParams(rule.params as Record<string, unknown>)
      : {}
    setCheckedFields(new Set(Object.keys(flatParams)))
    rowsInitializedRef.current = true
  }, [open, ruleLoading, rule, fields, cellInfo])

  const staging = useEditDocumentStaging({
    ruleId: rule?.id ?? '',
    ruleBase: PAR_RULE_BASE,
  })
  const [localFiles, setLocalFiles] = useState<File[]>([])

  const handleAddLocalFiles = useCallback((incoming: File[]) => {
    const valid = incoming.filter((f) => {
      if (!ALLOWED_MIME_TYPES.has(f.type)) {
        message.error(`${f.name}: unsupported file type.`)
        return false
      }
      const max = MAX_SIZE_BY_MIME[f.type] ?? 0
      if (f.size > max) {
        message.error(`${f.name}: file too large.`)
        return false
      }
      return true
    })
    setLocalFiles((prev) => {
      const names = new Set(prev.map((x) => x.name))
      return [...prev, ...valid.filter((x) => !names.has(x.name))].slice(0, 3)
    })
  }, [])

  const [isSaving, setIsSaving] = useState(false)
  const savingRef = useRef(false)

  const isApproved = rule?.status === PlanAndRateOverrideRuleStatus.APPROVED

  const handleToggle = useCallback(
    (field: string, checked: boolean) => {
      if (isApproved) return
      setCheckedFields((prev) => {
        const next = new Set(prev)
        checked ? next.add(field) : next.delete(field)
        return next
      })
    },
    [isApproved]
  )

  const handleSelect = useCallback(
    (field: string, src: PlanAndRateOverrideValueSource) => {
      setFocusField(src === SRC_OVERRIDE ? field : null)
      if (src === SRC_OVERRIDE) {
        setRows((prev) => {
          const existing = prev[field]
          const prefill =
            existing?.overrideValue !== null &&
            existing?.overrideValue !== undefined
              ? existing.overrideValue
              : ((getStagingValue(field, cellInfo?.rowData ?? {}) as
                  | string
                  | number
                  | boolean
                  | null) ?? null)
          return { ...prev, [field]: { source: src, overrideValue: prefill } }
        })
        return
      }
      const rawVal =
        src === SRC_IDEON
          ? getStagingValue(field, ideonData)
          : getStagingValue(field, realData)
      setRows((prev) => ({
        ...prev,
        [field]: {
          source: src,
          overrideValue: rawVal as string | number | boolean | null,
        },
      }))
    },
    [cellInfo, ideonData, realData]
  )

  const handleOverrideChange = useCallback(
    (field: string, v: string | number | boolean | null) => {
      setRows((prev) => ({
        ...prev,
        [field]: { ...prev[field], overrideValue: v },
      }))
    },
    []
  )

  const handleClose = useCallback(() => {
    if (isSaving) return
    setRows({})
    setFocusField(null)
    setCheckedFields(new Set())
    setLocalFiles([])
    staging.discard()
    onClose()
  }, [isSaving, staging, onClose])

  const save = useCallback(
    async (targetStatus: PlanAndRateOverrideRuleStatus) => {
      if (!cellInfo || !identity || savingRef.current) return
      savingRef.current = true
      setIsSaving(true)
      try {
        const selectedFlat: Record<string, unknown> = {}
        const selectedSources: Record<string, string> = {}

        for (const [field, rs] of Object.entries(rows)) {
          if (!checkedFields.has(field)) continue
          let rawValue: unknown
          if (rs.source === SRC_IDEON) {
            rawValue = getStagingValue(field, ideonData)
          } else if (rs.source === SRC_REAL) {
            rawValue = getStagingValue(field, realData)
          } else {
            rawValue = rs.overrideValue
          }
          selectedFlat[field] = coerceParamValue(field, rawValue)
          selectedSources[field] = rs.source
        }

        if (targetStatus === PlanAndRateOverrideRuleStatus.APPROVED) {
          const existingFlat = rule?.params
            ? flattenRuleParams(rule.params as Record<string, unknown>)
            : {}
          for (const field of Object.keys(FIELD_CONFIG)) {
            if (IDENTITY_FIELDS.has(field) || field in selectedFlat) continue
            if (existingFlat[field] !== undefined) {
              selectedFlat[field] = coerceParamValue(field, existingFlat[field])
              selectedSources[field] =
                (
                  rule?.paramSources as
                    | Record<string, PlanAndRateOverrideValueSource>
                    | undefined
                )?.[field] ?? SRC_OVERRIDE
            } else {
              const stagingVal = getStagingValue(field, ideonData)
              if (AREA_FACTOR_KEYS.has(field)) {
                // Always include every area factor key — null if no staging data
                selectedFlat[field] = coerceParamValue(field, stagingVal)
                selectedSources[field] = SRC_IDEON
              } else if (stagingVal !== null && stagingVal !== undefined) {
                selectedFlat[field] = coerceParamValue(field, stagingVal)
                selectedSources[field] = SRC_IDEON
              }
            }
          }
        }

        const existingFlat = rule?.params
          ? flattenRuleParams(rule.params as Record<string, unknown>)
          : {}
        const existingSources = (rule?.paramSources ?? {}) as Record<
          string,
          string
        >
        const mergedFlat = { ...existingFlat, ...selectedFlat }
        const mergedSources = { ...existingSources, ...selectedSources }

        const existingFlatKeys = Object.keys(existingFlat)
        for (const field of existingFlatKeys) {
          if (!checkedFields.has(field)) {
            delete mergedFlat[field]
            delete mergedSources[field]
          }
        }

        const mergedParams = nestRuleParams(mergedFlat)

        let ruleId: string
        if (!rule) {
          const created = await createPlanAndRateOverrideRule({
            template: 'PARTIAL_FIELD_UPDATE',
            match: identity,
            params: mergedParams,
            paramSources: mergedSources as Record<
              string,
              PlanAndRateOverrideValueSource
            >,
          })
          ruleId = created.id
        } else {
          await updatePlanAndRateOverrideRule(rule.id, {
            params: mergedParams,
            paramSources: mergedSources as Record<
              string,
              PlanAndRateOverrideValueSource
            >,
          })
          ruleId = rule.id
        }

        const currentRuleStatus =
          rule?.status ?? PlanAndRateOverrideRuleStatus.DRAFT
        if (targetStatus !== currentRuleStatus) {
          await updatePlanAndRateOverrideRuleStatus(ruleId, {
            status: targetStatus,
          })
        }

        if (rule && staging.hasStagedChanges) {
          await staging.commit()
        } else if (localFiles.length > 0) {
          await Promise.all(
            localFiles.map(async (file) => {
              const { documentId, presignedUploadUrl: uploadUrl } =
                await presignUploadUrl(
                  ruleId,
                  {
                    fileName: file.name,
                    contentType: file.type,
                    fileSize: file.size,
                  },
                  PAR_RULE_BASE
                )
              const result = await uploadToS3({
                presignedUrl: uploadUrl,
                file,
                contentType: file.type,
              })
              if (!result.success)
                throw result.error ?? new Error('S3 upload failed')
              await completeUpload(
                ruleId,
                documentId,
                { sizeBytes: file.size },
                PAR_RULE_BASE
              )
            })
          )
        }

        await queryClient.invalidateQueries({
          queryKey: [FIND_PLAN_AND_RATE_RULE_QUERY_KEY],
        })
        const rowId = cellInfo.rowData.id as string
        onSaved?.(rowId, {
          params: mergedParams,
          paramSources: mergedSources,
          status: targetStatus,
        })
        handleClose()
        const verb =
          targetStatus === PlanAndRateOverrideRuleStatus.APPROVED
            ? 'approved'
            : targetStatus === PlanAndRateOverrideRuleStatus.REJECTED
              ? 'rejected'
              : 'saved'
        message.success(`Override ${verb}.`)
      } catch {
        message.error('Failed to save override. Please try again.')
      } finally {
        savingRef.current = false
        setIsSaving(false)
      }
    },
    [
      cellInfo,
      identity,
      rows,
      checkedFields,
      rule,
      staging,
      localFiles,
      ideonData,
      realData,
      queryClient,
      onSaved,
      handleClose,
    ]
  )

  const hasRealData = useMemo(
    () =>
      fields.some((f) => {
        const v = getStagingValue(f, realData)
        return v !== null && v !== undefined && v !== ''
      }),
    [fields, realData]
  )

  useEffect(() => {
    if (!open) return
    const html = document.documentElement
    const orig = html.style.overflow
    html.style.overflow = 'hidden'
    return () => {
      html.style.overflow = orig
    }
  }, [open])

  const statusLabel = rule?.status ?? 'Draft'

  const approveBtn = (label = 'Approve Row as Override') => (
    <Button
      type="primary"
      size="small"
      icon={<CheckOutlined />}
      style={{ background: '#10b981', borderColor: '#10b981' }}
      onClick={() => save(PlanAndRateOverrideRuleStatus.APPROVED)}
      loading={isSaving}
      disabled={isSaving}
    >
      {label}
    </Button>
  )

  const saveDraftBtn = (label = 'Save') => (
    <Button
      size="small"
      icon={<SendOutlined />}
      onClick={() => save(PlanAndRateOverrideRuleStatus.DRAFT)}
      loading={isSaving}
      disabled={isSaving}
    >
      {label}
    </Button>
  )

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={null}
      closable={false}
      width={720}
      footer={null}
      centered
      destroyOnClose
      transitionName="ant-fade"
      maskTransitionName="ant-fade"
      styles={{
        body: {
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '82vh',
          overflow: 'hidden',
        },
      }}
    >
      {ruleLoading && open ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spin />
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          {/* ── Top pinned: header + status strip ── */}
          <div style={{ padding: '20px 20px 0', flexShrink: 0 }}>
            {/* ── Header ── */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 14,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: BRAND_LIGHT,
                  color: BRAND,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 15,
                  flexShrink: 0,
                }}
              >
                <TableOutlined />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    fontSize: 11,
                    fontWeight: 600,
                    color: BRAND,
                    marginBottom: 1,
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: BRAND,
                    }}
                  />
                  Plans &amp; Rates
                </div>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: '#0f172a',
                    lineHeight: 1.25,
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 6,
                    flexWrap: 'wrap',
                  }}
                >
                  {identity
                    ? `${identity.hiosPlanId} - ${identity.state}`
                    : 'Override'}
                  {identity && (
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: '#94a3b8',
                      }}
                    >
                      {identity.year}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={handleClose}
                aria-label="Close"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#94a3b8',
                  fontSize: 14,
                  padding: 2,
                  flexShrink: 0,
                }}
              >
                <CloseOutlined />
              </button>
            </div>

            {/* ── Status strip ── */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 0,
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                padding: '8px 14px',
                marginBottom: 14,
                background: '#f8fafc',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={COL_LABEL_STYLE}>Status</span>
                <StatusPill status={statusLabel} />
              </div>
            </div>
          </div>
          {/* end top pinned */}

          {/* ── Scrollable: comparison table ── */}
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              padding: '12px 20px 0',
            }}
          >
            <div
              style={{
                flex: 1,
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                overflowY: 'auto',
                marginBottom: 10,
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    background: '#f8fafc',
                  }}
                >
                  <tr style={{ background: '#f8fafc' }}>
                    <th
                      style={{
                        ...COL_LABEL_STYLE,
                        padding: '8px 12px',
                        width: 36,
                        textAlign: 'center',
                      }}
                    >
                      Overridden
                    </th>
                    <th
                      style={{
                        ...COL_LABEL_STYLE,
                        padding: '8px 12px',
                        textAlign: 'left',
                      }}
                    >
                      Plan &amp; Rate Field
                    </th>
                    <th
                      style={{
                        ...COL_LABEL_STYLE,
                        padding: '8px 12px',
                        textAlign: 'left',
                        minWidth: 100,
                      }}
                    >
                      Ideon Record
                    </th>
                    {hasRealData && (
                      <th
                        style={{
                          ...COL_LABEL_STYLE,
                          padding: '8px 12px',
                          textAlign: 'left',
                          minWidth: 100,
                        }}
                      >
                        Sureco Record
                      </th>
                    )}
                    <th
                      style={{
                        ...COL_LABEL_STYLE,
                        padding: '8px 12px',
                        textAlign: 'left',
                        minWidth: 150,
                      }}
                    >
                      Change Record
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((field) => {
                    const rs = rows[field]
                    if (!rs) return null
                    return (
                      <FieldRow
                        key={field}
                        field={field}
                        isChecked={checkedFields.has(field)}
                        isReadOnly={isApproved}
                        rowState={rs}
                        ideonValue={getStagingValue(field, ideonData)}
                        realValue={getStagingValue(field, realData)}
                        showRealData={hasRealData}
                        autoFocusOverride={focusField === field}
                        onToggle={handleToggle}
                        onSelect={handleSelect}
                        onOverrideChange={handleOverrideChange}
                      />
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {/* end scrollable */}

          {/* ── Bottom pinned: info + evidence + footer ── */}
          <div style={{ flexShrink: 0, padding: '0 20px 20px' }}>
            {/* ── Info line ── */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 11,
                color: '#94a3b8',
                marginBottom: 8,
                marginTop: 10,
              }}
            >
              <InfoCircleOutlined />
              Click a value to choose its source for each field, or pick Change
              Data to enter a custom value.
            </div>

            {/* ── Evidence ── */}
            <div style={{ marginBottom: 10 }}>
              <InlineEvidenceSection
                ruleId={rule?.id ?? null}
                staging={staging}
                localFiles={localFiles}
                isReadOnly={isApproved}
                ruleBase={PAR_RULE_BASE}
                onAddLocal={handleAddLocalFiles}
                onRemoveLocal={(idx) =>
                  setLocalFiles((p) => p.filter((_, i) => i !== idx))
                }
              />
            </div>

            {/* ── Footer ── */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                borderTop: '1px solid #e5e7eb',
                paddingTop: 14,
              }}
            >
              {isApproved ? (
                <>
                  <span
                    style={{
                      fontSize: 12,
                      color: '#94a3b8',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      flex: 1,
                    }}
                  >
                    <CheckOutlined style={{ color: '#16a34a' }} />
                    Approved — read only
                  </span>
                  {saveDraftBtn('Send back to draft')}
                </>
              ) : (
                <>
                  {approveBtn()}
                  {saveDraftBtn()}
                </>
              )}
            </div>
          </div>
          {/* end bottom pinned */}
        </div>
      )}
    </Modal>
  )
}
